import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { addMonths, startOfMonth } from 'date-fns';
import { formatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { ProductTypeEnum } from '../../../model/enums/ProductTypeEnum';
import { BaseOption } from '../../../model/BaseOption';
import { ApplicationApiService } from '../../../services/applicationApiService';
import { MainOccupationDto } from '../../../model/business/application/MainOccupationDto';
import { ComponentCommunicationService } from '../../../component-communication.service';
import { EnumConverterService } from '../../../services/EnumConverterService';
import { ValidationService } from '../../../services/ValidationService';
import { ConfigSettingDto } from '../../../model/business/ConfigSettingDto';
import { SessionService } from '../../../session-service.service';
import { FontTypeEnum } from 'src/app/model/enums/FontTypeEnum';
import { ClaimDurationEnum } from 'src/app/model/enums/ClaimDurationEnum';
import NavigationSettings, { StepsGG } from '../../../components/navigation-component/navigation-settings.service';
import { SessionGgService } from '../../../services/session-gg.service';
import { QuotationRequestDto } from '../../../model/business/calculation/QuotationRequestDto';
import { CalculationApiService } from '../../../services/calculationApiService';
import { CalculationResponseResultPremiumDto } from '../../../model/business/calculation/CalculationResponseResultPremiumDto';
import { CalculatorComponent } from '../../../calculator/calculator-component/calculator.component';
import { NOTIFICATION_TYPE, NotificationEvent, NotificationService } from '../../../services/NotificationService';
import { InsurerEnum } from '../../../model/enums/InsurerEnum';
import { CalculationGgService } from '../../../services/calculation-gg.service';
import { CalculationRequestDto } from '../../../model/business/CalculationRequestDto';
import { PolicyTerms } from '../../../model/enums/PolicyTermsEnum';
import { Constants } from '../../../constants';
import { replaceThousandSeparatorAndRemovingEuroPrefix } from '../../../utils/utils';
import { Subscription } from 'rxjs';
import { TemplateService } from '../../../services/TemplateService';
import { OptionsEnum } from '../../../model/enums/OptionsEnum';
import { DateConverterService } from '../../../date-converter.service';

type ShowFormValidityOptions = {
  showResult?: boolean;
};

@Component({
  selector: 'app-calculate-page-gg',
  templateUrl: './calculation-page.component.html',
  styleUrls: ['./calculation-page.component.css'],
})

export class CalculationPageGGComponent implements OnInit, OnDestroy {
  productType = ProductTypeEnum.GG;
  selectOccupationsList: BaseOption[];
  occupationsList: MainOccupationDto[];
  insuredOccupation: MainOccupationDto;
  form;
  durationList: Array<BaseOption>;
  datesArray: BaseOption[] = [];
  maximumMonths = 3;
  minDate = startOfMonth(addMonths(new Date(), 1));
  locale: string;
  FontTypeEnum = FontTypeEnum;
  sumAssuredMinValue = 100;
  sumAssuredMaxValue = 5000;
  maxStartAge = 62;
  maxEndAge = 67;
  premiums: CalculationResponseResultPremiumDto | undefined;
  showCalculator = false;
  calculatorValues = null;
  notificationEvent: NotificationEvent;
  notificationSubscription: Subscription;
  premiumsInsurerList = this.enumConverterService.toBaseList('InsurerEnum', InsurerEnum);
  selectedBestPremium = {insurer: '', premium: '', policyTerms: ''};
  relativePath: string;
  premiumSubscription: Subscription;

  thousandSeparator = Constants.THOUSAND_SEPARATOR;
  withoutOwnLaborError = false;

  constructor(
    private applicationApiService: ApplicationApiService,
    private formBuilder: FormBuilder,
    private componentCommunication: ComponentCommunicationService,
    private validationService: ValidationService,
    private enumConverterService: EnumConverterService,
    private translationService: TranslateService,
    private sessionService: SessionService,
    private sessionGgService: SessionGgService,
    private navigationSettings: NavigationSettings,
    private calculationApiService: CalculationApiService,
    private dialogRef: MatDialog,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private calculationGgService: CalculationGgService,
    private notificationService: NotificationService,
    private templateService: TemplateService,
    private dateConverterService: DateConverterService,
  ) {
    const {
      productType,
      locale,
    } = this.activatedRoute.snapshot.params;

    this.relativePath = locale ?
      `/${locale}/${productType}` :
      `/${productType}`;

    this.durationList = this.sessionGgService.getDurationList || this.enumConverterService.toBaseList('DurationEnum', ClaimDurationEnum);
    this.initDataFromSession();
  }

  ngOnInit(): void {
    this.getOccupationList();
    this.definePossibleDateOfCommencements();
    this.listenOccupationChanges();
    this.updatePremiumValues();
    this.listenToNotifications();
  }

  listenToNotifications(): void {
    this.notificationSubscription = this.notificationService.register().subscribe((event) => {
      this.notificationEvent = event;
    });
  }

  ngOnDestroy(): void {
    this.premiumSubscription.unsubscribe();
    this.notificationSubscription.unsubscribe();
    this.sessionGgService.setDurationList(this.durationList);
  }

  getOccupationList(): void {
    this.applicationApiService
      .getOccupations('GG')
      .subscribe((occupations: MainOccupationDto[]) => {
        const baseOptions: BaseOption[] = [];
        this.occupationsList = occupations;
        occupations.forEach((occupation: MainOccupationDto) => {
          baseOptions.push(new BaseOption(occupation.occupation.name));
        });
        this.selectOccupationsList = baseOptions;
      });
  }

  definePossibleDateOfCommencements(): void {
    this.locale = this.translationService.currentLang || this.translationService.defaultLang;

    for (let i = 0; i < this.maximumMonths; i++) {
      const date = startOfMonth(addMonths(this.minDate, i));
      this.datesArray.push({
        text: formatDate(date, 'longDate', this.locale),
        value: this.dateConverterService.convertDate(date).toISOString(),
      });
    }
  }

  listenOccupationChanges(): void {
    this.form.controls.occupation.valueChanges.subscribe(value => {
      this.occupationChanged(value);
      this.form.controls
        .dateOfBirth.setValidators([Validators.required, this.validationService.dateOfBirthValidator(
          this.maxStartAge, this.maxEndAge
        )]
      );
      this.updateDurationOptions();
    });
  }

  occupationFieldChange(): void {
    this.withoutOwnLaborError = false;
    this.form.get('occupation').setValidators([Validators.required, this.validationService.occupationValidator()]);
  }

  get isWithoutOwnLaborError(): boolean {
    const isNoPrevErrors = this.form.occupation?.errors === null;
    return !isNoPrevErrors && this.withoutOwnLaborError;
  }

  setActiveStep(): void {
    this.navigationSettings.setActiveState(StepsGG.CALCULATE, true);
  }

  updateDurationOptions(): void {
    let insuredFullYears = 0;
    const dob = this.form.controls.dateOfBirth.value;
    if (dob) {
      const today = new Date();
      const maxDateForStartPolicy = new Date(today.setMonth(today.getMonth() + this.maximumMonths));
      insuredFullYears = maxDateForStartPolicy.getFullYear() - dob.getFullYear();
    }

    this.durationList = this.durationList.map((duration) => {
      if (insuredFullYears && duration.value === 'YEARS_2') {
        duration.disabled = insuredFullYears + 2 > this.maxEndAge;
      }
      if (insuredFullYears && duration.value === 'YEARS_5') {
        duration.disabled = insuredFullYears + 5 > this.maxEndAge;
      }
      if (insuredFullYears && duration.value === 'YEARS_10') {
        duration.disabled = insuredFullYears + 10 > this.maxEndAge;
      }
      if (duration.value === 'INSURANCE_END_DATE') {
        duration.text = this.templateService.parseTemplatedStatement('DurationEnum.OCCUPATION_YEARS_MAX', 'years', {years: this.maxEndAge});
      }
      return duration;
    });
  }

  get dateMaskFormat(): string {
    return this.componentCommunication.getDateFormat();
  }

  get configSettings(): ConfigSettingDto {
    return this.sessionService.getConfigSettingsDto();
  }

  occupationChanged(value: string): void {
    if (value) {
      const occupation = this.occupationsList.find(item => item.occupation.name === value);
      this.insuredOccupation = occupation;
      this.maxStartAge = occupation.maxStartAge || this.maxStartAge;
      this.maxEndAge = occupation.maxEndAgeOwnLabour || this.maxEndAge;
      if (occupation.ownLabour === OptionsEnum.No.toUpperCase()) {
        this.withoutOwnLaborError = true;
        this.form.get('occupation').clearValidators();
      }
    }
  }

  private getEndDate(): Date {
    const date = new Date(this.form.value.dateOfBirth);
    const { maxEndAgeOwnLabour } = this.insuredOccupation;
    date.setFullYear(date.getFullYear() + maxEndAgeOwnLabour);
    date.setDate(0);
    return date;
  }

  createCalculationRequestDTO(): any {
    const calculationRequestDto = new CalculationRequestDto(ProductTypeEnum.GG, PolicyTerms.QL_GG_2020_06);
    calculationRequestDto.dateOfCommencement = this.form.value.dateOfCommencement;
    calculationRequestDto.insureds = [{
      covers: [this.getCover()],
      dateOfBirth: this.form.value.dateOfBirth,
      isSmoker: false,
      gender: '',
    }];
    calculationRequestDto.variables = this.getRequestVariables();

    return calculationRequestDto;
  }

  doCalculation(): void {
    if (this.isFormValid() && !this.withoutOwnLaborError) {
      this.notificationEvent = {
        type: NOTIFICATION_TYPE.HIDDEN,
      };
      this.premiums = undefined;
      const calculationRequestDto = this.createCalculationRequestDTO();
      this.sessionService.setCalculationRequest(calculationRequestDto);
      this.calculationGgService.requestCalculateGG();
      const calculationPageValues = this.sessionGgService.getCalculationPageValues();
      this.sessionGgService.setCalculationPageValues({
        ...calculationPageValues,
        ...this.form.value,
        insuredOccupation: this.insuredOccupation,
      });
    }
  }

  handleSubmitCalculate(): void {
    if (this.premiums && this.isFormValid() && !this.withoutOwnLaborError) {
      this.navigationSettings.setActiveState(StepsGG.CALCULATE, false);
      this.navigationSettings.setFinishedState(StepsGG.CALCULATE, true);
      this.router.navigate([this.relativePath + '/application']);
    } else {
      this.doCalculation();
    }
  }

  downloadQuote(): void {
    const quotationRequestDto = new QuotationRequestDto();
    quotationRequestDto.calculationRequestDto = this.sessionService.getCallculationRequest();
    quotationRequestDto.premiumResponse = this.premiums;
    quotationRequestDto.intermediaryName = this.sessionService.intermediaryName;
    quotationRequestDto.intermediaryEmail = this.sessionService.intermediaryEmail;
    quotationRequestDto.intermediaryPhone = this.sessionService.intermediaryPhone;
    this.calculationApiService
      .getQuotation(quotationRequestDto)
      .subscribe((response) => {
        const byteCharacters = response.pdfContent;
        const byteArray = new Uint8Array(
          atob(byteCharacters)
            .split('')
            .map((char) => char.charCodeAt(0))
        );
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const nav = (window.navigator as any);
        if (nav && nav.msSaveOrOpenBlob) {
          const fileName = 'Offerte.pdf';
          nav.msSaveOrOpenBlob(blob, fileName);
          return;
        }
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL);
      });
  }

  public toggleCalculator(): void {
    const dialogRef = this.dialogRef.open(CalculatorComponent, {
      height: '800px',
      width: '600px',
      data: this.sessionGgService.getCalculatorFormValues || this.calculatorValues,
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((cbData) => {
      const {
        amount = 0,
        formValues = null,
      } = cbData || {};
      if (formValues) {
        this.calculatorValues = formValues;
        this.form.controls.sumAssured.setValue(amount);
        this.sessionGgService.setCalculatorFormValues({...formValues, amount});
      }
    });
  }

  isFormValid(options: ShowFormValidityOptions = { showResult: true }): boolean {
    if (
      this.form.get('occupation').valid &&
      this.form.get('dateOfBirth').valid &&
      this.form.get('sumAssured').valid &&
      this.form.get('dateOfCommencement').valid &&
      this.form.get('claimDuration').valid
    ) {
      return true;
    }

    if (options.showResult) {
      this.form.markAllAsTouched();
      this.translationService.get('Common.ValidationFailedMessage').subscribe((message) => {
        this.notificationEvent = {
          type: NOTIFICATION_TYPE.WARNING,
          messages: [message],
        };
      });
    }
    return false;
 }

  get calculationExist(): boolean {
    return !!this.premiums;
  }

  initDataFromSession(): void {
    const {
      dateOfCommencement,
      sumAssured,
      claimDuration,
      dateOfBirth,
      insuredOccupation,
      occupation,
    } = this.sessionGgService.getCalculationPageValues();

    this.form = this.formBuilder.group({
      occupation: [occupation, [Validators.required, this.validationService.occupationValidator()]],
      dateOfBirth: [dateOfBirth, [Validators.required, this.validationService.dateOfBirthValidator(
        this.maxStartAge, this.maxEndAge
      )]],
      sumAssured: [sumAssured, [Validators.required,
        this.validationService.monthlyAmountValidator(this.sumAssuredMinValue, this.sumAssuredMaxValue, this.thousandSeparator)]
      ],
      dateOfCommencement: [dateOfCommencement, Validators.required],
      claimDuration: [claimDuration, Validators.required],
    });

    this.insuredOccupation = insuredOccupation;

    this.form.valueChanges.subscribe(() => {
      if (this.premiums) {
        this.doCalculation();
      }
    });
  }

  get buttonTitle(): string {
    if (this.premiums) {
      return 'ApplicationPage.Next';
    }
    return 'Calculation.Calculate';
  }

  updatePremiumValues(): void {
    this.premiumSubscription = this.sessionService.getCalculationResponse.subscribe(value => {
      if (value.premium > 0) {
        this.premiums = value;
      }
    });
  }

  private getCover(): any {
    return (
      {
        endDate: this.getEndDate(),
        startDate: this.form.value.dateOfCommencement,
        sumAssured: replaceThousandSeparatorAndRemovingEuroPrefix(this.form.value.sumAssured),
        variables: this.getCoverVariables(),
      });
  }

  private getRequestVariables(): any {
    return ({
      indexationPercentage: {
        type: 'string',
        value: '0'
      },
      indexationUsage: {
        type: 'string',
        value: 'NO_INDEXATION'
      },
      mentalIllnessIncluded: {
        type: 'boolean',
        value: 'true'
      },
      occupationClass: {
        type: 'string',
        value: this.insuredOccupation.classNr
      },
    });
  }

  private getCoverVariables(): any {
    return ( {
      claimDuration: {
        type: 'string',
        value: this.form.controls.claimDuration.value
      },
      mentalIllnessIncluded: {
        type: 'boolean',
        value: 'false'
      },
      waitingPeriod: {
        type: 'string',
        value: 'DAYS_90'
      },
      typeOfCover: {
        type: 'string',
        value: 'LEVEL_COVER',
      },
    });
  }

  get isSecondColumnShownForMobile(): boolean {
    const isEventPresent = this.notificationEvent ? this.notificationEvent.type !== NOTIFICATION_TYPE.HIDDEN : false;
    return this.calculationExist || isEventPresent;
  }
}
